import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  LuTrash2,
  LuX,
} from "react-icons/lu";
import AccountActionButton from "./AccountActionButton";
import { translateLabel, useTranslationSnapshot } from "./../../translations/runtime";


function DeleteAccountModal({
  loading = false,
  onClose,
  onConfirm,
  open = false,
}) {
  useTranslationSnapshot();
  // =========================================================
  // ESCAPE KEY
  // =========================================================
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !loading
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    loading,
    onClose,
    open,
  ]);

  // =========================================================
  // DO NOT RENDER WHEN CLOSED
  // =========================================================
  if (
    !open ||
    typeof document === "undefined"
  ) {
    return null;
  }

  // =========================================================
  // BACKDROP CLICK
  // =========================================================
  const handleBackdropClick = () => {
    if (loading) {
      return;
    }

    onClose?.();
  };

  // =========================================================
  // MODAL CLICK
  // =========================================================
  const handleModalClick = (event) => {
    event.stopPropagation();
  };

  // =========================================================
  // CONFIRM DELETE
  //
  // THIS CALLS handleConfirmDelete
  // FROM DeleteAccountPanel.
  //
  // THAT FUNCTION CALLS THE DELETE API.
  // =========================================================
  const handleDeleteClick = () => {
    if (loading) {
      return;
    }

    onConfirm?.();
  };

  return createPortal(
    <div
      className="vendor-delete-account-modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        aria-label={translateLabel("txt_web_vendor_delete_account_confirmation", "Delete account confirmation")}
        aria-modal="true"
        className="vendor-delete-account-modal"
        onClick={handleModalClick}
        role="dialog"
      >
        <button
          aria-label={translateLabel("txt_web_vendor_close_delete_account", "Close delete account")}
          className="vendor-delete-account-modal-close"
          disabled={loading}
          onClick={onClose}
          type="button"
        >
          <LuX aria-hidden="true" />
        </button>

        <div className="vendor-delete-account-modal-icon">
          <LuTrash2 aria-hidden="true" />
        </div>

        <h2 className="vendor-delete-account-modal-title"> {translateLabel("txt_end_user_delete_account", "Delete Account")} </h2>

        <p className="vendor-delete-account-modal-description">
          {translateLabel("txt_web_vendor_confirm_account_deletion", "Are you sure you want to delete this account?")}
        </p>

        <div className="vendor-delete-account-modal-actions">
          <AccountActionButton
            color="danger"
            disabled={loading}
            onClick={handleDeleteClick}
            type="button"
            width="72px"
          >
            {loading
              ? translateLabel("txt_end_user_deleting", "Deleting...")
              : translateLabel("lnk_delete", "Delete")}
          </AccountActionButton>

          <AccountActionButton
            color="dark"
            disabled={loading}
            onClick={onClose}
            type="button"
            width="72px"
          > {translateLabel("txt_end_user_later", "Later")} </AccountActionButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default DeleteAccountModal;
